import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import site from './data/site.js';

const DRAG_THRESHOLD = 5;
const HUB_R = 96;
const DOMAIN_R_CLOSED = 200;
const DOMAIN_R_OPEN_BASE = 250;
const COL_W = 168; // horizontal brick step
const ROW_GAP = 14;

function domainAngles(domains, openDomains) {
  // Open branches claim more angular width so neighbors adjust.
  const weights = domains.map((d) => {
    if (!openDomains.has(d.id)) return 1;
    const n = (d.nodes || []).length;
    return 1.35 + Math.min(n, 8) * 0.12;
  });
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const coll = domains.findIndex((d) => d.id === 'collection');
  const collI = coll >= 0 ? coll : 0;

  // Place collection near lower-right (~0.65 rad) for first-frame framing.
  const targetColl = 0.65;
  let collCenter = 0;
  let acc = 0;
  weights.forEach((w, i) => {
    const span = (w / sum) * Math.PI * 2;
    const mid = acc + span / 2;
    if (i === collI) collCenter = mid;
    acc += span;
  });
  const rot = targetColl - collCenter;

  const angles = [];
  acc = 0;
  weights.forEach((w) => {
    const span = (w / sum) * Math.PI * 2;
    angles.push(rot + acc + span / 2);
    acc += span;
  });
  return angles;
}

function leafSize(child) {
  let h = 32;
  let w = 150;
  if (child.subtitle) h += 12;
  if (child.feature || child.proof || child.outcome) {
    h += 76;
    w = 176;
  } else if (child.why) {
    h += 50;
    w = 170;
  } else if (child.note) {
    h += 38;
    w = 160;
  }
  if (child.image || child.video) {
    h += 70;
    w = Math.max(w, 160);
  }
  return { w, h: h + ROW_GAP };
}

/**
 * Brick stagger: alternate columns (horizontal) and rows (vertical).
 * Origin sits outward from the domain along its ray.
 */
function placeLeavesBrick(domainX, domainY, angle, children) {
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const tx = -Math.sin(angle); // tangent
  const ty = Math.cos(angle);
  const sizes = children.map(leafSize);

  // Prefer world-aligned brick for readable heights, skewed by ray.
  const cols = children.length <= 3 ? 1 : children.length <= 6 ? 2 : 2;
  const rows = Math.ceil(children.length / cols);

  // Measure grid
  const colHeights = Array.from({ length: cols }, () => 0);
  const cells = children.map((child, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    return { child, i, col, row, ...sizes[i] };
  });

  // row height = max in that row
  const rowH = Array.from({ length: rows }, () => 0);
  cells.forEach((c) => {
    rowH[c.row] = Math.max(rowH[c.row], c.h);
  });
  const totalH = rowH.reduce((a, b) => a + b, 0);
  const totalW = cols * COL_W;

  // Anchor: outward from domain, grid centered on ray
  const baseDist = 110 + Math.min(children.length, 6) * 6;
  const ax = domainX + ux * baseDist;
  const ay = domainY + uy * baseDist;

  // Local axes: +x along tangent (horizontal-ish stagger), +y along radial-ish mix with world Y
  return cells.map((c) => {
    let xOff = (c.col - (cols - 1) / 2) * COL_W;
    // brick: odd rows shift half column for horizontal stagger
    if (c.row % 2 === 1) xOff += COL_W * 0.42;
    // vertical stagger within column
    let yOff = -totalH / 2;
    for (let r = 0; r < c.row; r += 1) yOff += rowH[r];
    yOff += rowH[c.row] * 0.35;

    // Map local offset: blend world axes so Y really changes height on screen
    const x = ax + xOff * Math.max(Math.abs(tx), 0.55) * Math.sign(tx || 1) + ux * (c.row * 10);
    // Force distinct screen heights
    const y = ay + yOff;

    // Small extra diagonal nudge
    const diag = (c.i % 3) * 12;
    return {
      x: x + (c.col === 0 ? -diag * 0.3 : diag * 0.3),
      y: y + (c.col % 2) * 8,
      w: c.w,
      h: c.h,
    };
  });
}

/** Global separation: open nodes push others on X and Y; domains also move. */
function reflowSeparate(nodes, fixedIds = new Set(['home'])) {
  const movable = nodes.filter((n) => n.kind === 'domain' || n.kind === 'leaf');
  for (let iter = 0; iter < 18; iter += 1) {
    for (let i = 0; i < movable.length; i += 1) {
      for (let j = i + 1; j < movable.length; j += 1) {
        const a = movable[i];
        const b = movable[j];
        const aw = a.w || (a.kind === 'domain' ? 120 : 150);
        const bw = b.w || (b.kind === 'domain' ? 120 : 150);
        const ah = a.h || (a.kind === 'domain' ? 36 : 70);
        const bh = b.h || (b.kind === 'domain' ? 36 : 70);
        const minX = (aw + bw) * 0.52;
        const minY = (ah + bh) * 0.48;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        if (adx >= minX || ady >= minY) continue;

        const pushX = (minX - adx) / 2 + 1;
        const pushY = (minY - ady) / 2 + 1;
        // Prefer the axis with more penetration to resolve
        const xFirst = minX - adx < minY - ady;
        const sx = dx === 0 ? 1 : Math.sign(dx);
        const sy = dy === 0 ? 1 : Math.sign(dy);

        const aFixed = fixedIds.has(a.id);
        const bFixed = fixedIds.has(b.id);
        const aMul = aFixed ? 0 : bFixed ? 1 : 0.5;
        const bMul = bFixed ? 0 : aFixed ? 1 : 0.5;

        if (xFirst) {
          a.x -= pushX * sx * aMul;
          b.x += pushX * sx * bMul;
          a.y -= pushY * sy * aMul * 0.55;
          b.y += pushY * sy * bMul * 0.55;
        } else {
          a.y -= pushY * sy * aMul;
          b.y += pushY * sy * bMul;
          a.x -= pushX * sx * aMul * 0.55;
          b.x += pushX * sx * bMul * 0.55;
        }
      }
    }
  }

  // Keep hubs between home and domains
  const home = nodes.find((n) => n.kind === 'home');
  if (home) {
    nodes.forEach((n) => {
      if (n.kind !== 'hub') return;
      const domainId = n.id.replace(/^hub::/, '');
      const d = nodes.find((x) => x.id === domainId);
      if (!d) return;
      n.x = home.x + (d.x - home.x) * 0.42;
      n.y = home.y + (d.y - home.y) * 0.42;
    });
  }
}

function buildLayout(domains, openDomains) {
  const CX = 0;
  const CY = 0;
  const nodes = [{ id: 'home', x: CX, y: CY, kind: 'home', w: 260, h: 120 }];
  const edges = [];
  const angles = domainAngles(domains, openDomains);
  const n = domains.length;

  domains.forEach((domain, di) => {
    const angle = angles[di];
    const hubId = `hub::${domain.id}`;
    const isOpen = openDomains.has(domain.id);
    const childN = (domain.nodes || []).length;
    const radius = isOpen
      ? DOMAIN_R_OPEN_BASE + Math.min(childN, 8) * 14
      : DOMAIN_R_CLOSED;

    const dx = CX + Math.cos(angle) * radius;
    const dy = CY + Math.sin(angle) * radius;

    nodes.push({
      id: hubId,
      x: CX + Math.cos(angle) * HUB_R,
      y: CY + Math.sin(angle) * HUB_R,
      kind: 'hub',
      angle,
    });

    nodes.push({
      id: domain.id,
      x: dx,
      y: dy,
      kind: 'domain',
      domain,
      angle,
      isOpen,
      w: 130,
      h: 40,
    });
    edges.push({ from: hubId, to: domain.id });

    if (!isOpen) return;

    const children = domain.nodes || [];
    const poses = placeLeavesBrick(dx, dy, angle, children);
    children.forEach((child, ci) => {
      const id = `${domain.id}::${child.id}`;
      const p = poses[ci];
      nodes.push({
        id,
        x: p.x,
        y: p.y,
        w: p.w,
        h: p.h,
        kind: 'leaf',
        domainId: domain.id,
        child,
        angle,
      });
      edges.push({ from: domain.id, to: id });
    });
  });

  for (let i = 0; i < n; i += 1) {
    edges.push({
      from: domains[i].id,
      to: domains[(i + 1) % n].id,
      weak: true,
    });
  }

  reflowSeparate(nodes);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  nodes.forEach((node) => {
    if (node.kind === 'hub') return;
    const padX = (node.w || 100) * 0.55 + 24;
    const padY = (node.h || 40) * 0.55 + 24;
    minX = Math.min(minX, node.x - padX);
    maxX = Math.max(maxX, node.x + padX);
    minY = Math.min(minY, node.y - padY);
    maxY = Math.max(maxY, node.y + padY);
  });

  const pad = 56;
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;
  const width = Math.max(900, Math.ceil(maxX - minX));
  const height = Math.max(700, Math.ceil(maxY - minY));

  nodes.forEach((node) => {
    node.x -= minX;
    node.y -= minY;
  });

  return { nodes, edges, width, height };
}

function stemPath(a, b) {
  // Straight elastic stem — stretches with node drag
  return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
}

/** Keep hub on the home→domain segment so radial stems stay woven while dragging. */
function withLiveHubs(nodes) {
  const byId = {};
  nodes.forEach((n) => {
    byId[n.id] = n;
  });
  const home = byId.home;
  if (!home) return nodes;
  return nodes.map((n) => {
    if (n.kind !== 'hub') return n;
    const domainId = n.id.replace(/^hub::/, '');
    const domain = byId[domainId];
    if (!domain) return n;
    return {
      ...n,
      x: home.x + (domain.x - home.x) * 0.42,
      y: home.y + (domain.y - home.y) * 0.42,
    };
  });
}

const Dot = ({ active = false }) => (
  <span className={`dot${active ? ' is-on' : ''}`} aria-hidden="true" />
);
Dot.propTypes = { active: PropTypes.bool };

function lineDetail(child) {
  if (child.feature) return child.feature;
  if (child.why) return child.why;
  if (child.note) return child.note;
  if (child.outcome) return child.outcome;
  return child.subtitle || '';
}

function CreditLine({ credit }) {
  if (!credit?.url || !credit?.label) return null;
  return (
    <span className="credit-line">
      {credit.prefix || 'In collaboration with '}
      <a
        href={credit.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(ev) => ev.stopPropagation()}
      >
        {credit.label}
      </a>
      .
    </span>
  );
}
CreditLine.propTypes = {
  credit: PropTypes.shape({
    prefix: PropTypes.string,
    label: PropTypes.string,
    url: PropTypes.string,
  }),
};

/** Prefer still/gif on mobile; video often fails or is heavy. */
function MediaThumb({ image, video, title, className }) {
  if (image?.url) {
    return (
      <img
        className={className}
        src={image.url}
        alt={image.alt || title || ''}
        loading="lazy"
        decoding="async"
      />
    );
  }
  if (video?.url) {
    return (
      <video
        className={className}
        controls
        muted
        playsInline
        preload="metadata"
        poster={image?.url}
      >
        <source src={video.url} type={video.type || 'video/mp4'} />
      </video>
    );
  }
  return null;
}
MediaThumb.propTypes = {
  image: PropTypes.shape({ url: PropTypes.string, alt: PropTypes.string }),
  video: PropTypes.shape({ url: PropTypes.string, type: PropTypes.string }),
  title: PropTypes.string,
  className: PropTypes.string,
};

const AsciiTree = ({ openDomains, onToggle }) => (
  <div className="ascii-page">
    <pre className="ascii-tree">
      <span className="ascii-name">{site.name.toUpperCase()}</span>
      {'\n'}
      <span className="ascii-blurb">{site.blurb}</span>
      {'\n'}
      <span className="ascii-links">
        {site.links.map((l, i) => (
          <span key={l.url}>
            {i > 0 ? ' · ' : ''}
            <a href={l.url}>{l.label}</a>
          </span>
        ))}
      </span>
      {'\n\n'}
      {site.domains.map((d) => {
        const open = openDomains.has(d.id);
        const kids = d.nodes || [];
        return (
          <span key={d.id} className="ascii-branch">
            <button
              type="button"
              className="ascii-domain"
              onClick={() => onToggle(d.id)}
              aria-expanded={open}
            >
              {open ? '▼' : '▶'} {d.title.toUpperCase()}
              <span className="ascii-desc"> — {d.description}</span>
            </button>
            {'\n'}
            {open &&
              kids.map((c, i) => {
                const last = i === kids.length - 1;
                const branch = last ? '└─' : '├─';
                const pipe = last ? '  ' : '│ ';
                const detail = lineDetail(c);
                const hasMedia = !!(c.image || c.video);
                return (
                  <span key={c.id} className="ascii-leaf">
                    {`  ${branch} `}
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noopener noreferrer">
                        {c.title}
                      </a>
                    ) : (
                      c.title
                    )}
                    {c.subtitle ? (
                      <span className="ascii-muted">{` · ${c.subtitle}`}</span>
                    ) : null}
                    {'\n'}
                    {detail ? (
                      <span className="ascii-muted">{`  ${pipe}  ${detail}\n`}</span>
                    ) : null}
                    {c.credit ? (
                      <span className="ascii-muted">
                        {`  ${pipe}  `}
                        <CreditLine credit={c.credit} />
                        {'\n'}
                      </span>
                    ) : null}
                    {hasMedia ? (
                      <span className="ascii-media-line">
                        {`  ${pipe}  `}
                        <MediaThumb
                          image={c.image}
                          video={c.image ? null : c.video}
                          title={c.title}
                          className="ascii-media"
                        />
                        {'\n'}
                      </span>
                    ) : null}
                  </span>
                );
              })}
            {'\n'}
          </span>
        );
      })}
    </pre>
  </div>
);

AsciiTree.propTypes = {
  openDomains: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

const App = () => {
  const [openDomains, setOpenDomains] = useState(() => new Set(['collection']));
  const [focus, setFocus] = useState('home');
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(false);
  const shellRef = useRef(null);
  const dragRef = useRef(null);
  const layoutSize = useRef({ w: 1200, h: 900 });
  const didCenter = useRef(false);
  const nodesRef = useRef([]);
  const rafRef = useRef(0);

  const base = useMemo(
    () => buildLayout(site.domains, openDomains),
    [openDomains]
  );

  layoutSize.current = { w: base.width, h: base.height };

  const nodes = useMemo(() => {
    const placed = base.nodes.map((n) => {
      const p = positions[n.id];
      return p ? { ...n, x: p.x, y: p.y } : n;
    });
    // Hubs track home↔domain so stems stretch instead of detaching
    return withLiveHubs(placed);
  }, [base.nodes, positions]);
  nodesRef.current = nodes;

  const byId = useMemo(() => {
    const m = {};
    nodes.forEach((n) => {
      m[n.id] = n;
    });
    return m;
  }, [nodes]);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell || didCenter.current) return;
    const home = byId.home;
    const coll = byId.collection;
    if (!home) return;
    let tx = home.x;
    let ty = home.y;
    if (coll) {
      tx = home.x * 0.55 + coll.x * 0.45;
      ty = home.y * 0.55 + coll.y * 0.45;
    }
    const space = shell.querySelector('.web-space');
    if (!space) return;
    const scale =
      space.getBoundingClientRect().width / Math.max(space.offsetWidth, 1) || 1;
    shell.scrollLeft = Math.max(0, tx * scale - shell.clientWidth / 2);
    shell.scrollTop = Math.max(0, ty * scale - shell.clientHeight / 2);
    didCenter.current = true;
  }, [byId]);

  const toggleDomain = useCallback((id) => {
    setOpenDomains((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setFocus(id);
    setPositions({});
  }, []);

  const onPointerDown = useCallback(
    (event, id) => {
      if (event.target.closest('a, video, input, img')) return;
      const node = byId[id];
      if (!node || node.kind === 'hub') return;
      const space = event.currentTarget.closest('.web-space');
      if (!space) return;
      event.preventDefault();

      const rect = space.getBoundingClientRect();
      const { w, h } = layoutSize.current;
      const sx = w / Math.max(rect.width, 1);
      const sy = h / Math.max(rect.height, 1);
      const px = (event.clientX - rect.left) * sx;
      const py = (event.clientY - rect.top) * sy;

      const snapshot = {};
      nodesRef.current.forEach((n) => {
        snapshot[n.id] = { x: n.x, y: n.y };
      });

      let mode = 'node';
      let groupIds = [id];

      if (node.kind === 'home') {
        // Center grab: entire spiderweb translates as one rigid body
        mode = 'web';
        groupIds = nodesRef.current.map((n) => n.id);
      } else if (node.kind === 'domain') {
        // Domain strand moves with its leaves; edges to home stretch
        mode = 'strand';
        const did = node.domain && node.domain.id;
        groupIds = nodesRef.current
          .filter(
            (n) =>
              n.id === id ||
              n.id === 'hub::' + did ||
              (n.kind === 'leaf' && n.domainId === did)
          )
          .map((n) => n.id);
      }

      dragRef.current = {
        mode,
        id,
        kind: node.kind,
        domainId: node.kind === 'domain' ? (node.domain && node.domain.id) : node.domainId,
        groupIds,
        snapshot,
        grabX: px - node.x,
        grabY: py - node.y,
        sx,
        sy,
        rectLeft: rect.left,
        rectTop: rect.top,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        lastEvent: null,
      };
      setFocus(id);
    },
    [byId]
  );

  const flushDrag = useCallback((event) => {
    const d = dragRef.current;
    if (!d || !d.moved) return;

    const px = (event.clientX - d.rectLeft) * d.sx;
    const py = (event.clientY - d.rectTop) * d.sy;
    const { w, h } = layoutSize.current;

    if (d.mode === 'web') {
      const home0 = d.snapshot.home;
      if (!home0) return;
      const nx = px - d.grabX;
      const ny = py - d.grabY;
      const shiftX = nx - home0.x;
      const shiftY = ny - home0.y;
      const next = {};
      Object.keys(d.snapshot).forEach((gid) => {
        const o = d.snapshot[gid];
        next[gid] = { x: o.x + shiftX, y: o.y + shiftY };
      });
      setPositions(next);
      return;
    }

    const anchor = d.snapshot[d.id];
    if (!anchor) return;
    let nx = px - d.grabX;
    let ny = py - d.grabY;
    nx = Math.max(8, Math.min(w - 8, nx));
    ny = Math.max(8, Math.min(h - 8, ny));
    const shiftX = nx - anchor.x;
    const shiftY = ny - anchor.y;

    setPositions((prev) => {
      const next = { ...prev };
      d.groupIds.forEach((gid) => {
        const o = d.snapshot[gid];
        if (!o) return;
        next[gid] = { x: o.x + shiftX, y: o.y + shiftY };
      });
      return next;
    });
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = event.clientX - d.startX;
      const dy = event.clientY - d.startY;
      if (!d.moved && dx * dx + dy * dy > DRAG_THRESHOLD * DRAG_THRESHOLD) {
        d.moved = true;
        setDragging(true);
      }
      if (!d.moved) return;
      d.lastEvent = event;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const cur = dragRef.current;
        if (cur && cur.lastEvent) flushDrag(cur.lastEvent);
      });
    },
    [flushDrag]
  );

  const onPointerUp = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const d = dragRef.current;
    if (!d) return;
    if (d.lastEvent && d.moved) flushDrag(d.lastEvent);
    if (!d.moved && d.kind === 'domain' && d.domainId) {
      toggleDomain(d.domainId);
    }
    dragRef.current = null;
    setDragging(false);
  }, [flushDrag, toggleDomain]);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onPointerMove, onPointerUp]);

  return (
    <>
      <AsciiTree openDomains={openDomains} onToggle={toggleDomain} />

      <div
        className={
          'web-page web-page--desktop' + (dragging ? ' is-dragging' : '')
        }
      >
        <div className="sr-only">
          <h1>{site.name}</h1>
          <p>{site.blurb}</p>
        </div>

        <div className="web-shell" ref={shellRef}>
          <div
            className="web-space"
            style={{ width: base.width, height: base.height }}
          >
            <svg
              className="web-lines"
              width={base.width}
              height={base.height}
              viewBox={'0 0 ' + base.width + ' ' + base.height}
              aria-hidden="true"
            >
              {base.edges.map((e) => {
                const a = byId[e.from];
                const b = byId[e.to];
                if (!a || !b) return null;
                return (
                  <path
                    key={e.from + '-' + e.to + '-' + (e.weak ? 'w' : 's')}
                    d={stemPath(a, b)}
                    className={e.weak ? 'web-edge web-edge--weak' : 'web-edge'}
                  />
                );
              })}
            </svg>

            {nodes.map((n) => {
              if (n.kind === 'hub') return null;

              if (n.kind === 'home') {
                return (
                  <div
                    key={n.id}
                    className={
                      'web-node web-node--home' +
                      (focus === 'home' ? ' is-focus' : '')
                    }
                    style={{ left: n.x, top: n.y }}
                    onPointerDown={(e) => onPointerDown(e, n.id)}
                  >
                    <div className="bio-core">
                      <h1>{site.name}</h1>
                      <p>{site.blurb}</p>
                      <p className="bio-core__links">
                        {site.links.map((l, i) => (
                          <span key={l.url}>
                            {i > 0 ? ' · ' : ''}
                            <a href={l.url}>{l.label}</a>
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                );
              }

              if (n.kind === 'domain') {
                const d = n.domain;
                const isOpen = openDomains.has(d.id);
                return (
                  <div
                    key={n.id}
                    data-domain={d.id}
                    className={
                      'web-node web-node--domain' +
                      (isOpen ? ' is-open' : '') +
                      (focus === d.id ? ' is-focus' : '')
                    }
                    style={{ left: n.x, top: n.y }}
                    onPointerDown={(e) => onPointerDown(e, n.id)}
                  >
                    <div className="web-domain-hit" role="button" tabIndex={0}>
                      <Dot active={isOpen || focus === d.id} />
                      <span className="web-label">
                        <strong>{d.title}</strong>
                        <em>{d.description}</em>
                      </span>
                    </div>
                  </div>
                );
              }

              const c = n.child;
              const hasMedia = !!(c.image || c.video);
              const hasCard = !!(
                c.feature ||
                c.proof ||
                c.outcome ||
                c.why ||
                c.note ||
                c.credit
              );

              return (
                <div
                  key={n.id}
                  className={
                    'web-node web-node--leaf is-open' +
                    (focus === n.id ? ' is-focus' : '')
                  }
                  style={{ left: n.x, top: n.y }}
                  onPointerDown={(e) => onPointerDown(e, n.id)}
                >
                  <div
                    className="web-leaf-hit"
                    role="button"
                    tabIndex={0}
                    onClick={() => setFocus(n.id)}
                  >
                    <Dot active={focus === n.id} />
                    <span className="web-label">
                      <strong>
                        {c.url ? (
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            {c.title}
                          </a>
                        ) : (
                          c.title
                        )}
                      </strong>
                      {c.subtitle && <em>{c.subtitle}</em>}
                    </span>
                  </div>

                  {hasCard && (
                    <div className="web-card">
                      {c.feature && (
                        <p>
                          <span className="k">feature</span>
                          {c.feature}
                        </p>
                      )}
                      {c.proof && (
                        <p>
                          <span className="k">proof</span>
                          {c.proof}
                        </p>
                      )}
                      {c.outcome && (
                        <p>
                          <span className="k">outcome</span>
                          {c.outcome}
                        </p>
                      )}
                      {c.why && (
                        <p>
                          <span className="k">why</span>
                          {c.why}
                        </p>
                      )}
                      {c.note && <p>{c.note}</p>}
                      {c.credit && (
                        <p className="web-credit">
                          <CreditLine credit={c.credit} />
                        </p>
                      )}
                    </div>
                  )}

                  {hasMedia && (
                    <div className="web-media">
                      <MediaThumb
                        image={c.image}
                        video={c.video}
                        title={c.title}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default App;
